import { useEffect } from 'react';
import { useNavigate, useLocation } from "@/lib/router-compat";
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import Home from './Home';
import { clearAdminFlowState, hasAdminMfaVerified } from '@/lib/adminAuthState';
import { hasConciergeIntent, hasConciergeLocalDraft } from '@/lib/conciergeRouting';

const hasPendingVerification = (): boolean => {
  try {
    const email = sessionStorage.getItem('pending_verification_email');
    if (email) {
      const status = sessionStorage.getItem('pending_verification_status');
      if (status === 'failed') return false;
      return true;
    }
  } catch { /* no-op */ }
  try {
    const email = localStorage.getItem('pending_verification_email');
    if (email) {
      const status = localStorage.getItem('pending_verification_status');
      if (status === 'failed') return false;
      return true;
    }
  } catch { /* no-op */ }
  return false;
};

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, isEmailVerified } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();

  const hasAuthCallbackPayload =
    location.hash.includes('access_token') ||
    location.hash.includes('refresh_token') ||
    location.hash.includes('type=email_change') ||
    location.search.includes('code=') ||
    location.search.includes('error=');

  useEffect(() => {
    if (location.pathname === '/' && hasAuthCallbackPayload) {
      console.info('[Index] Auth callback payload detected on root, forwarding to /auth/callback');
      navigate(`/auth/callback${location.search}${location.hash}`, { replace: true });
    }
  }, [hasAuthCallbackPayload, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (hasAuthCallbackPayload || authLoading || profileLoading) return;

    if (location.pathname === '/' && !hasAdminMfaVerified()) {
      clearAdminFlowState();
    }

    // If there's a pending verification email but no user/session,
    // redirect to verify-email so they don't see the landing page
    if (!user && hasPendingVerification()) {
      navigate('/verify-email', { replace: true });
      return;
    }

    if (!user) return;

    if (!isEmailVerified) {
      navigate('/verify-email', { replace: true });
      return;
    }

    // NEVER auto-redirect to admin from the homepage.
    // Admin access must be explicitly requested via /admin or /admin/login.
    // The homepage always shows the consumer flow.

    // Consumer flow gates
    if (profile?.account_status === 'waitlisted') {
      navigate('/waitlist', { replace: true });
    } else if (!profile?.onboarding_completed) {
      // Authenticated + email-verified but onboarding not done → push into onboarding
      navigate('/onboarding', { replace: true });
    } else if (!profile?.identity_verified) {
      // Identity verification is REQUIRED after onboarding — no bypass
      navigate('/verify-profile', { replace: true });
    } else if (hasConciergeIntent(profile) || hasConciergeLocalDraft()) {
      navigate('/concierge-submitted', { replace: true });
    } else if (!profile?.plan_selected) {
      navigate('/choose-plan', { replace: true });
    } else {
      navigate('/discover', { replace: true });
    }
  }, [user, authLoading, isEmailVerified, profile, profileLoading, navigate]);

  // Render the public homepage immediately — including during SSR — so first
  // paint never waits on auth resolution, network calls, or JS boot, and the
  // page stays fully readable even if a browser fails to run the bundle.
  // Signed-in members are redirected by the effect above once auth resolves.
  return <Home />;
};

export default Index;
